"use client";

/**
 * MeetingLaunchWatchdog
 *
 * Bridges two cron transitions that happen in Firestore without Pusher:
 *   scheduled → launching  (cron fires at meeting_time - 15 min)
 *   ongoing   → completed/incompleted (cron fires at meeting_time + duration)
 *
 * Strategy:
 *  - For scheduled meetings: poll 2 min after the cron is expected to run
 *    (meeting_time - 13 min), retry every 5 min up to MAX_LAUNCH_RETRIES.
 *  - For ongoing meetings: poll 2 min after the expected end time
 *    (meeting_time + duration + 2 min), retry every 5 min.
 *  - A periodic sweep (every 2 min) re-evaluates timers so meetings that
 *    weren't in the look-ahead window on mount are caught automatically.
 *  - On tab focus: immediately poll any overdue meetings as a fallback.
 */

import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSession } from "next-auth/react";
import {
  setMeeting,
  fetchMeetingsThunk,
} from "@/store/apps/meetings/MeetingDataSlice";

// Cron transitions scheduled→launching at meeting_time - 15 min.
// Start polling at that exact offset; 90 s retries catch it within one cycle.
const LAUNCH_POLL_OFFSET_MS = -15 * 60_000; // meeting_time - 15 min

// Retry interval and max retries for scheduled→launching.
// Cron runs every 2 min so retry often — worst case the first poll fires just
// before the cron cycle and we want to catch it within ~90 s.
const LAUNCH_RETRY_MS = 90_000; // 90 seconds
const MAX_LAUNCH_RETRIES = 20; // covers up to ~meeting_time + 17 min

// Completion poll: 2 min after session end time.
const COMPLETION_GRACE_MS = 2 * 60_000;
const COMPLETION_RETRY_MS = 2 * 60_000; // 2 minutes
// Must cover all three cron completion paths:
//   launching → incompleted : scheduledEnd + 3 min
//   ongoing → completed Case 1: scheduledEnd + 10-12 min (both inactive)
//   ongoing → completed Case 2: scheduledEnd + 15-17 min (time-based)
// With COMPLETION_GRACE_MS=2 min start and 2 min retries, 8 retries → T+18.
const MAX_COMPLETION_RETRIES = 8;

// Look-ahead window for scheduling launch timers.
const LOOK_AHEAD_MS = 60 * 60_000; // 60 minutes ahead

// How often the sweep re-evaluates all meetings for missing timers.
const SWEEP_INTERVAL_MS = 2 * 60_000;

async function fetchMeetingStatus(meetingId) {
  const res = await fetch(`/api/meeting/get-meeting/${meetingId}`, {
    cache: "no-store",
  });
  if (!res.ok) return null;
  const json = await res.json();
  return json?.meeting ?? null;
}

// The single-meeting API returns raw Firestore data where Timestamps are
// serialized as { _seconds, _nanoseconds }.  Normalize to ISO strings so
// dispatching setMeeting doesn't corrupt Redux (which expects ISO strings
// matching the fetchMeetingsThunk output).
function normalizeMeetingTimestamps(meeting) {
  return JSON.parse(
    JSON.stringify(meeting, (_, value) => {
      if (
        value &&
        typeof value === "object" &&
        typeof value._seconds === "number"
      ) {
        return new Date(
          value._seconds * 1000 +
            Math.floor((value._nanoseconds || 0) / 1_000_000),
        ).toISOString();
      }
      return value;
    }),
  );
}

function getMeetingTime(meeting) {
  const mt = meeting.session?.meeting_time || meeting.meeting_time;
  if (!mt) return null;
  if (mt instanceof Date) return mt.getTime();
  // Firestore Timestamp serialized as { _seconds, _nanoseconds }
  if (mt && typeof mt === "object" && typeof mt._seconds === "number") {
    return mt._seconds * 1000 + Math.floor((mt._nanoseconds || 0) / 1_000_000);
  }
  const ts = new Date(mt).getTime();
  return isNaN(ts) ? null : ts;
}

export default function MeetingLaunchWatchdog() {
  const dispatch = useDispatch();
  const { data: session } = useSession();
  const meetings = useSelector((state) => state.meetings.data ?? []);

  // Always-current ref so timer callbacks don't close over stale meetings.
  const meetingsRef = useRef(meetings);
  meetingsRef.current = meetings;

  // Timer handles: "launch:<id>" and "complete:<id>"
  const timersRef = useRef({});
  // Retry counts: same key scheme
  const retriesRef = useRef({});

  // ─── Poll helpers (via refs so callbacks are always fresh) ───────────────

  const pollLaunch = useRef(null);
  pollLaunch.current = async (meetingId) => {
    const fresh = await fetchMeetingStatus(meetingId);
    if (!fresh) return;

    const newStatus = fresh.status?.toLowerCase();

    if (newStatus && newStatus !== "scheduled") {
      dispatch(setMeeting(normalizeMeetingTimestamps(fresh)));
      delete timersRef.current[`launch:${meetingId}`];
      delete retriesRef.current[`launch:${meetingId}`];
      return;
    }

    // Still scheduled — retry
    const key = `launch:${meetingId}`;
    retriesRef.current[key] = (retriesRef.current[key] ?? 0) + 1;
    if (retriesRef.current[key] < MAX_LAUNCH_RETRIES) {
      timersRef.current[key] = setTimeout(
        () => pollLaunch.current(meetingId),
        LAUNCH_RETRY_MS,
      );
    } else {
      delete timersRef.current[key];
      delete retriesRef.current[key];
    }
  };

  const pollCompletion = useRef(null);
  pollCompletion.current = async (meetingId) => {
    const fresh = await fetchMeetingStatus(meetingId);
    if (!fresh) return;

    const newStatus = fresh.status?.toLowerCase();

    if (newStatus && newStatus !== "ongoing" && newStatus !== "launching") {
      dispatch(setMeeting(normalizeMeetingTimestamps(fresh)));
      delete timersRef.current[`complete:${meetingId}`];
      delete retriesRef.current[`complete:${meetingId}`];
      return;
    }

    // Still active — retry
    const key = `complete:${meetingId}`;
    retriesRef.current[key] = (retriesRef.current[key] ?? 0) + 1;
    if (retriesRef.current[key] < MAX_COMPLETION_RETRIES) {
      timersRef.current[key] = setTimeout(
        () => pollCompletion.current(meetingId),
        COMPLETION_RETRY_MS,
      );
    } else {
      delete timersRef.current[key];
      delete retriesRef.current[key];
    }
  };

  // ─── Core sweep: register timers for any meeting that needs one ──────────

  const runSweep = useRef(null);
  runSweep.current = () => {
    if (!session?.user?.sub) return;
    const now = Date.now();
    const current = meetingsRef.current;

    current.forEach((meeting) => {
      const id = meeting.id || meeting.request_id;
      if (!id) return;

      const status = meeting.status?.toLowerCase();
      const ts = getMeetingTime(meeting);
      if (!ts) return;

      // ── scheduled → launching ─────────────────────────────────────────
      if (status === "scheduled") {
        const launchKey = `launch:${id}`;
        if (timersRef.current[launchKey] != null) return; // already armed

        const fireAt = ts + LAUNCH_POLL_OFFSET_MS; // meeting_time - 13 min
        if (fireAt > now + LOOK_AHEAD_MS) return; // too far ahead

        const delay = Math.max(0, fireAt - now);
        timersRef.current[launchKey] = setTimeout(
          () => pollLaunch.current(id),
          delay,
        );
      }

      // ── ongoing → completed/incompleted ──────────────────────────────
      if (status === "ongoing" || status === "launching") {
        const completeKey = `complete:${id}`;
        if (timersRef.current[completeKey] != null) return; // already armed

        const durationMs = (meeting.session?.duration || 30) * 60_000;
        const endAt = ts + durationMs + COMPLETION_GRACE_MS;
        const delay = Math.max(0, endAt - now);
        timersRef.current[completeKey] = setTimeout(
          () => pollCompletion.current(id),
          delay,
        );
      }
    });

    // Clean up launch timers for meetings no longer scheduled
    const scheduledIds = new Set(
      current
        .filter((m) => m.status?.toLowerCase() === "scheduled")
        .map((m) => m.id || m.request_id),
    );
    Object.keys(timersRef.current).forEach((key) => {
      if (!key.startsWith("launch:")) return;
      const id = key.slice("launch:".length);
      if (!scheduledIds.has(id)) {
        clearTimeout(timersRef.current[key]);
        delete timersRef.current[key];
        delete retriesRef.current[key];
      }
    });
  };

  // ─── Run sweep on mount, on meetings change, and on a 2-min interval ────

  useEffect(() => {
    if (!session?.user?.sub) return;
    runSweep.current();
  }, [meetings, session?.user?.sub]);

  useEffect(() => {
    if (!session?.user?.sub) return;
    const interval = setInterval(() => runSweep.current(), SWEEP_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [session?.user?.sub]);

  // ─── Tab focus fallback ──────────────────────────────────────────────────

  useEffect(() => {
    if (!session?.user?.sub) return;

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;

      // Re-run sweep immediately so timers are current
      runSweep.current();

      // Also force-poll any ongoing/launching meetings right now
      const hasActive = meetingsRef.current.some((m) => {
        const s = m.status?.toLowerCase();
        return s === "ongoing" || s === "launching";
      });
      if (hasActive) {
        dispatch(fetchMeetingsThunk());
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [session?.user?.sub, dispatch]);

  // ─── Cleanup all timers on unmount ──────────────────────────────────────

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);

  return null;
}
